import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, Check, X, Plus, Copy, ImageIcon, Trash2, ArrowUp, ArrowDown, Eye, Trash, LayoutGrid, List } from 'lucide-react';
import QuestionRenderer from '../../pages/quiz/components/QuestionRenderer';
import QuizQuestionTitle from './QuizQuestionTitle';
import QuillEditorWithImageResize from '../QuillEditorWithImageResize';
import CodeEditorWithMonaco from './CodeEditorWithMonaco';
import TestCaseManager, { TestCase } from './TestCaseManager';
import ScoringPointsEditor, { StructuredScoringConfig } from './ScoringPointsEditor';
import KnowledgePointSelector from './KnowledgePointSelector';
import QuestionPreviewModal from './QuestionPreviewModal';
import ImageUploadModal from '../ImageUploadModal';
import { getSupabaseUrl } from '../../supabase/client';

interface Option {
  id: string;
  label: string;
  text: string;
  isCorrect: boolean;
  // 旧字段保留兼容
  image?: string;        // 单张图片(向后兼容)
  imageWidth?: number;   // 单张图片宽度

  // 新字段:多张图片支持
  images?: string[];           // 图片URL数组
  imageWidths?: number[];      // 对应每张图片的宽度百分比数组
}

interface Question {
  id: string;
  type: string;
  subType?: string;
  title: string;
  name?: string;
  content?: string; // 题干内容（富文本）
  options: Option[];
  score: number;
  titleImages?: string[];
  titleImageWidths?: number[];
  titleImageLayout?: string;
  imagePosition?: string;
  optionLayout?: string;
  optionImageWidths?: Record<string, number>;
  scratchSubtype?: string;
  referenceSb3Url?: string;
  // 编辑相关字段
  explanation?: string;
  answerText?: string;
  scoringPoints?: string;
  gradingMode?: 'oj_ai' | 'manual' | 'ai';
  showAnswer?: boolean;
  codeTemplate?: string;
  codeLanguage?: string;
  allowImageUpload?: boolean;
  maxImageSize?: number;
  allowTextInput?: boolean;
  maxTextLength?: number;
  allowFileUpload?: boolean;
  maxFileSize?: number;
  answerImages?: string[];
  answerImageWidths?: number[];
  scoringPointsImages?: string[];
  scoringPointsImageWidths?: number[];
  referenceAnswerImages?: string[];
  referenceAnswerImageWidths?: number[];
  scratchAnswerItems?: any[];
  scratchConfig?: any;
  // Python编辑器新增字段
  difficulty?: 'easy' | 'medium' | 'hard';
  knowledgePointIds?: string[];
  commonMistakes?: string;
  timeLimitMs?: number;
  memoryLimitMb?: number;
  testCases?: TestCase[];
  structuredScoring?: StructuredScoringConfig;
  [key: string]: any;
}

interface QuizStyleQuestionViewProps<T extends Question = Question> {
  question: T;
  currentIndex: number;
  totalCount: number;
  examQuestions: T[];
  onPrevQuestion: () => void;
  onNextQuestion: () => void;
  onExamQuestionsChange: (questions: T[]) => void;
  onEditQuestion?: (questionId: string) => void;
  hasWidthChanges: boolean;
  onSaveWidthSettings: () => void;
  isSavingWidths: boolean;

  // 编辑模式相关
  isEditing?: boolean;
  onExitEdit?: () => void;
  onSaveQuestion?: (question: T) => Promise<void>;
  onDeleteQuestion?: (questionId: string) => void;
  onCancelEdit?: () => void;
  onInsertQuestion?: (questionId: string) => void;
  onCopyQuestion?: (questionId: string) => void;

  // 保存题目布局相关
  onSaveQuestionLayout?: (questionId: string, layoutData: {
    titleImageWidths?: number[];
    titleImageLayout?: string;
    imagePosition?: string;
    optionImageWidths?: Record<string, number>;
    optionLayout?: string;
  }) => Promise<void>;
}

const typeNames: Record<string, string> = {
  single_choice: '单选题',
  multi_choice: '多选题',
  judge: '判断题',
  subjective: '主观题',
  python_coding: 'Python编程题',
  cpp_coding: 'C++编程题',
  scratch_coding: 'Scratch编程题',
};

const difficultyOptions = [
  { value: 'easy', label: '简单', color: 'bg-green-100 text-green-700' },
  { value: 'medium', label: '中等', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'hard', label: '困难', color: 'bg-red-100 text-red-700' },
];

const gradingModeOptions = [
  { value: 'oj_ai', label: 'OJ执行+AI辅助分析' },
  { value: 'manual', label: '人工判分' },
];

export default function QuizStyleQuestionView<T extends Question = Question>({
  question,
  currentIndex,
  totalCount,
  examQuestions,
  onPrevQuestion,
  onNextQuestion,
  onExamQuestionsChange,
  onEditQuestion,
  hasWidthChanges,
  onSaveWidthSettings,
  isSavingWidths,
  isEditing = false,
  onExitEdit,
  onSaveQuestion,
  onDeleteQuestion,
  onCancelEdit,
  onInsertQuestion,
  onCopyQuestion,
  onSaveQuestionLayout,
}: QuizStyleQuestionViewProps<T>) {
  // HMR 热更新期间 question 可能短暂为 undefined/null
  // 直接返回占位避免崩溃
  if (!question) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 flex items-center justify-center">
        <p className="text-gray-400 text-sm">加载中...</p>
      </div>
    );
  }

  // 防止组件卸载后执行异步操作
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  // 编辑草稿状态 - 使用key强制重新创建state,避免切换题目时的状态污染
  const [editDraft, setEditDraft] = useState<T>(question);
  const [showPreview, setShowPreview] = useState(false);

  // 选项图片上传相关状态
  const [showOptionImageModal, setShowOptionImageModal] = useState(false);
  const [optionImageUploadTarget, setOptionImageUploadTarget] = useState<{ optionId: string } | null>(null);
  const [uploadedOptionImages, setUploadedOptionImages] = useState<string[]>([]);

  // 当 question prop 变化时，重置草稿
  // 监听 question 对象本身的变化（不仅仅是 id），确保从编辑模式切回预览模式时
  // editDraft 能正确更新为包含最新布局字段的数据
  React.useEffect(() => {
    if (question && question.id) {
      setEditDraft(question);
    }
  }, [question]);

  // 更新草稿的辅助函数
  const updateDraft = (updates: Partial<Question>) => {
    setEditDraft(prev => ({ ...prev, ...updates } as T));
  };

  // 保存处理
  const handleSave = async () => {
    if (unmountedRef.current) return;
    try {
      // 先保存布局信息
      if (onSaveQuestionLayout) {
        await onSaveQuestionLayout(editDraft.id, {
          titleImageWidths: editDraft.titleImageWidths,
          titleImageLayout: editDraft.titleImageLayout,
          imagePosition: editDraft.imagePosition,
          optionImageWidths: editDraft.optionImageWidths,
          optionLayout: editDraft.optionLayout,
        });
      }

      if (unmountedRef.current) return;

      // 再保存题目内容
      await onSaveQuestion?.(editDraft);

      if (unmountedRef.current) return;

      // 同步更新 examQuestions，使预览模式能拿到最新的 name/content 等字段
      const newExamQuestions = examQuestions.map(q =>
        q.id === editDraft.id ? editDraft : q
      );
      onExamQuestionsChange(newExamQuestions);

      if (!unmountedRef.current) {
        onExitEdit?.();
      }
    } catch (error) {
      if (unmountedRef.current) return;
      console.error('保存题目失败:', error);
      alert('保存失败，请重试');
    }
  };

  // 取消处理
  const handleCancel = () => {
    setEditDraft(question);
    onExitEdit?.();
  };

  // 计算当前统一的缩放比例(用于高亮快捷按钮)
  // 返回 null 表示"自动适应"（没有人手动设定宽度），返回具体数值表示人为设定
  const currentUniformScale = React.useMemo(() => {
    if (!question?.options) return null;
    const optionsWithImages = question.options.filter(o => o.image || (o.images && o.images.length > 0));
    if (optionsWithImages.length === 0) return null;

    // 检查是否有人为设定的宽度值
    const manualWidths = optionsWithImages.map(o => {
      if (o.imageWidth != null) return o.imageWidth;
      if (o.imageWidths && o.imageWidths.length > 0 && o.imageWidths[0] != null) return o.imageWidths[0];
      return null; // 未设定 = 自动适应
    });

    // 如果所有图片都是自动适应，返回 null
    if (manualWidths.every(w => w === null)) return null;

    // 如果有人为设定的值且全部一致，返回该值
    const setWidths = manualWidths.filter(w => w !== null);
    if (setWidths.length > 0 && setWidths.every(w => w === setWidths[0])) return setWidths[0];

    // 不一致或部分设定，不高亮任何快捷按钮
    return null;
  }, [question?.options]);

  // 处理布局切换 - 仅修改布局，不强制修改图片缩放值，让图片自动适应容器
  const handleOptionLayoutChange = (layout: 'grid' | 'list') => {
    // 只更新 optionLayout，保留用户已设置的图片缩放值不变
    const updatedQuestion = { ...question, optionLayout: layout } as T;
    const newExamQuestions = examQuestions.map(q =>
      q.id === question.id ? updatedQuestion : q
    );
    onExamQuestionsChange(newExamQuestions);
    setEditDraft(updatedQuestion);
  };

  // 处理快捷缩放 - 同时更新 editDraft 和父组件数据
  const handleQuickScaleChange = (scale: number) => {
    const newOptions = question.options.map(o => ({
      ...o,
      imageWidth: o.image ? scale : o.imageWidth,
      imageWidths: o.images ? o.images.map(() => scale) : o.imageWidths
    }));

    // 构建 optionImageWidths 映射
    const newOptionImageWidths: Record<string, number> = {};
    newOptions.forEach(o => {
      const key = o.id || o.label || '';
      if (o.image || (o.images && o.images.length > 0)) {
        newOptionImageWidths[key] = o.imageWidth || (o.imageWidths && o.imageWidths.length > 0 ? o.imageWidths[0] : scale);
      }
    });

    // 同步更新到父组件的 examQuestions，使 question prop 变化
    const updatedQuestion = { ...question, options: newOptions, optionImageWidths: newOptionImageWidths } as T;
    const newExamQuestions = examQuestions.map(q =>
      q.id === question.id ? updatedQuestion : q
    );
    onExamQuestionsChange(newExamQuestions);

    // 同时更新 editDraft
    setEditDraft(updatedQuestion);
  };

  // 更新选项
  const updateOption = (optionId: string, updates: Partial<Option>) => {
    const newOptions = editDraft.options.map(o =>
      o.id === optionId ? { ...o, ...updates } : o
    );
    updateDraft({ options: newOptions });
  };

  // 设置正确答案（单选/判断）
  const setCorrectAnswer = (optionId: string) => {
    const newOptions = editDraft.options.map(o => ({
      ...o,
      isCorrect: o.id === optionId
    }));
    updateDraft({ options: newOptions });
  };

  // 切换正确答案（多选）
  const toggleCorrectAnswer = (optionId: string) => {
    const newOptions = editDraft.options.map(o =>
      o.id === optionId ? { ...o, isCorrect: !o.isCorrect } : o
    );
    updateDraft({ options: newOptions });
  };

  // 添加选项
  const addOption = () => {
    const newLabel = String.fromCharCode(65 + editDraft.options.length);
    updateDraft({
      options: [...editDraft.options, {
        id: newLabel,
        label: newLabel,
        text: '',
        isCorrect: false
      }]
    });
  };

  // 删除选项
  const removeOption = (optionId: string) => {
    const newOptions = editDraft.options
      .filter(o => o.id !== optionId)
      .map((o, idx) => ({
        ...o,
        id: String.fromCharCode(65 + idx),
        label: String.fromCharCode(65 + idx)
      }));
    updateDraft({ options: newOptions });
  };

  // 移动选项
  const moveOption = (index: number, direction: 'up' | 'down') => {
    const newOptions = [...editDraft.options];
    if (direction === 'up' && index > 0) {
      [newOptions[index], newOptions[index - 1]] = [newOptions[index - 1], newOptions[index]];
    } else if (direction === 'down' && index < newOptions.length - 1) {
      [newOptions[index], newOptions[index + 1]] = [newOptions[index + 1], newOptions[index]];
    }
    const relabeled = newOptions.map((o, idx) => ({
      ...o,
      id: String.fromCharCode(65 + idx),
      label: String.fromCharCode(65 + idx)
    }));
    updateDraft({ options: relabeled });
  };

  // 运行测试用例
  const handleRunTest = async (code: string, testCases: TestCase[]) => {
    if (unmountedRef.current) return;
    try {
      const supabaseUrl = getSupabaseUrl();
      const response = await fetch(`${supabaseUrl}/functions/v1/code-grading`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'validate_test_cases',
          code,
          testCases,
          timeLimitMs: editDraft.timeLimitMs || 1000,
          memoryLimitMb: editDraft.memoryLimitMb || 128,
        }),
      });

      if (unmountedRef.current) return;

      const result = await response.json();

      if (unmountedRef.current) return;

      if (!response.ok) {
        throw new Error(result.error || '执行失败');
      }

      // 显示测试结果
      const { summary, results } = result;
      let message = `测试完成!\n通过: ${summary.passed}/${summary.total}\n得分: ${summary.totalScore}/${summary.maxScore}`;

      if (results.length > 0) {
        message += '\n\n详细结果:';
        results.forEach((r: any) => {
          const status = r.passed ? '✓' : '✗';
          message += `\n${status} 用例${r.caseIndex + 1}: ${r.passed ? '通过' : '失败'}`;
          if (!r.passed && r.errorMessage) {
            message += ` - ${r.errorMessage}`;
          }
        });
      }

      if (!unmountedRef.current) {
        alert(message);
      }
    } catch (error) {
      if (unmountedRef.current) return;
      const message = error instanceof Error ? error.message : '未知错误';
      console.error('测试用例验证失败:', error);
      alert(`测试执行失败: ${message}`);
    }
  };

  // 选项图片上传处理
  const handleUploadOptionImages = (optionId: string) => {
    setOptionImageUploadTarget({ optionId });
    setUploadedOptionImages([]);
    setShowOptionImageModal(true);
  };

  const handleOptionImageUploadComplete = (urls: string[]) => {
    if (!optionImageUploadTarget) return;

    const { optionId } = optionImageUploadTarget;
    const option = editDraft.options.find(o => o.id === optionId);
    if (!option) return;

    // 合并现有图片和新上传的图片
    const existingImages = option.images || [];
    const existingWidths = option.imageWidths || [];
    const newImages = [...existingImages, ...urls];
    const newWidths = [...existingWidths, ...Array(urls.length).fill(100)];

    updateOption(optionId, { images: newImages, imageWidths: newWidths });
    setShowOptionImageModal(false);
    setOptionImageUploadTarget(null);
  };

  // 删除选项中的单张图片
  const removeOptionImage = (optionId: string, imageIndex: number) => {
    const option = editDraft.options.find(o => o.id === optionId);
    if (!option || !option.images) return;

    const newImages = option.images.filter((_, idx) => idx !== imageIndex);
    const newWidths = (option.imageWidths || []).filter((_, idx) => idx !== imageIndex);

    updateOption(optionId, { images: newImages, imageWidths: newWidths });
  };

  // 更新选项图片宽度
  const updateOptionImageWidth = (optionId: string, imageIndex: number, width: number) => {
    const option = editDraft.options.find(o => o.id === optionId);
    if (!option || !option.imageWidths) return;

    const newWidths = [...option.imageWidths];
    newWidths[imageIndex] = width;

    updateOption(optionId, { imageWidths: newWidths });
  };

  // 渲染客观题编辑面板
  const renderObjectiveEditor = () => {
    const isJudge = editDraft.type === 'judge';
    const isMulti = editDraft.type === 'multi_choice';

    return (
      <div className="mt-4 space-y-3">
        {editDraft.options.map((option, optIndex) => (
          <div key={option.id} className="border border-gray-200 rounded-lg p-3 bg-white">
            <div className="flex items-center gap-2">
              <button
                onClick={() => isMulti ? toggleCorrectAnswer(option.id) : setCorrectAnswer(option.id)}
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  option.isCorrect ? 'border-green-500 bg-green-500' : 'border-gray-300'
                }`}
              >
                {option.isCorrect && <Check className="w-4 h-4 text-white" />}
              </button>
              <span className="text-sm font-medium text-gray-600 w-6 shrink-0">{option.label}.</span>
              <input
                type="text"
                value={option.text}
                onChange={(e) => updateOption(option.id, { text: e.target.value })}
                placeholder={isJudge ? "选项内容" : `选项${optIndex + 1}`}
                disabled={isJudge}
                className={`w-full min-w-[200px] max-w-[400px] px-3 py-2 border border-gray-200 rounded text-sm focus:border-blue-500 focus:outline-none ${isJudge ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
              {/* 新增:上传图片按钮 */}
              <button
                onClick={() => handleUploadOptionImages(option.id)}
                disabled={isJudge}
                className={`p-1.5 rounded ${isJudge ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50'}`}
                title={isJudge ? "判断题不支持上传选项图片" : "为选项添加图片"}
              >
                <ImageIcon className="w-4 h-4" />
              </button>
              {optIndex > 0 && (
                <button onClick={() => moveOption(optIndex, 'up')} className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded" title="上移">
                  <ArrowUp className="w-4 h-4" />
                </button>
              )}
              {optIndex < editDraft.options.length - 1 && (
                <button onClick={() => moveOption(optIndex, 'down')} className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded" title="下移">
                  <ArrowDown className="w-4 h-4" />
                </button>
              )}
              {!isJudge && editDraft.options.length > 2 && (
                <button onClick={() => removeOption(option.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded" title="删除选项">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* 第二行:图片展示区域(如果有图片) - 一行2张网格布局 */}
            {(option.images && option.images.length > 0) && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                {option.images.map((imgUrl, imgIdx) => (
                  <div key={imgIdx} className="relative group">
                    <img
                      src={imgUrl.startsWith('/sb-api/') ? imgUrl : imgUrl.startsWith('/storage/') ? `${getSupabaseUrl()}${imgUrl}` : imgUrl}
                      style={{ width: `${option.imageWidths?.[imgIdx] || 100}%`, maxWidth: '100%', height: 'auto' }}
                      className="rounded border border-gray-200"
                      alt={`选项${option.label}图片${imgIdx + 1}`}
                    />
                    {/* 宽度调节滑块 */}
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="range"
                        min="10"
                        max="100"
                        value={option.imageWidths?.[imgIdx] || 100}
                        onChange={(e) => updateOptionImageWidth(option.id, imgIdx, parseInt(e.target.value))}
                        className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-gray-500 w-10 text-right">
                        {option.imageWidths?.[imgIdx] || 100}%
                      </span>
                      {/* 删除图片按钮 */}
                      <button
                        onClick={() => removeOptionImage(option.id, imgIdx)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                        title="删除此图片"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {!isJudge && (
          <button onClick={addOption} className="flex items-center gap-1 text-blue-500 text-sm hover:text-blue-600 mt-2">
            <Plus className="w-4 h-4" />添加选项
          </button>
        )}
        <div className="mt-4">
          <div className="text-sm font-medium text-gray-700 mb-2">答案解析（学生提交试卷后可见）</div>
          <textarea
            value={editDraft.explanation || ''}
            onChange={(e) => updateDraft({ explanation: e.target.value })}
            placeholder="请输入答案解析..."
            className="w-full h-24 px-3 py-2 border border-gray-200 rounded resize-none focus:border-blue-500 focus:outline-none bg-white text-sm"
          />
        </div>
      </div>
    );
  };

  // 渲染主观题编辑面板
  const renderSubjectiveEditor = () => {
    return (
      <div className="mt-4 space-y-4">
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">参考答案</div>
          <textarea
            value={editDraft.answerText || ''}
            onChange={(e) => updateDraft({ answerText: e.target.value })}
            placeholder="请输入参考答案..."
            className="w-full h-32 px-3 py-2 border border-gray-200 rounded resize-none focus:border-blue-500 focus:outline-none bg-white text-sm"
          />
        </div>
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">评分要点</div>
          <textarea
            value={editDraft.scoringPoints || ''}
            onChange={(e) => updateDraft({ scoringPoints: e.target.value })}
            placeholder="请输入评分要点..."
            className="w-full h-24 px-3 py-2 border border-gray-200 rounded resize-none focus:border-blue-500 focus:outline-none bg-white text-sm"
          />
        </div>
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">评分方式</div>
          <select
            value={editDraft.gradingMode || 'ai'}
            onChange={(e) => updateDraft({ gradingMode: e.target.value as any })}
            className="px-3 py-2 border border-gray-200 rounded text-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="ai">AI自动评分</option>
            <option value="manual">人工评分</option>
          </select>
        </div>
      </div>
    );
  };

  // 渲染编程题编辑面板（增强版）
  const renderCodingEditor = () => {
    const isPython = editDraft.type === 'python_coding';
    const isCpp = editDraft.type === 'cpp_coding';
    const language = isPython ? 'python' : isCpp ? 'cpp' : 'javascript';

    // 初始化结构化评分配置
    const structuredScoring = editDraft.structuredScoring || {
      points: [],
      commonMistakes: editDraft.commonMistakes || ''
    };

    return (
      <div className="mt-4 space-y-4">
        {/* 难度选择 */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">题目难度</div>
          <div className="flex gap-2">
            {difficultyOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => updateDraft({ difficulty: opt.value as any })}
                className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                  editDraft.difficulty === opt.value
                    ? opt.color + ' ring-2 ring-offset-1 ring-current'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* 知识点选择 */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">知识点</div>
          <KnowledgePointSelector
            selectedIds={editDraft.knowledgePointIds || []}
            onChange={(ids) => updateDraft({ knowledgePointIds: ids })}
          />
        </div>

        {/* 代码模板 - 使用Monaco Editor */}
        <CodeEditorWithMonaco
          value={editDraft.codeTemplate || ''}
          onChange={(value) => updateDraft({ codeTemplate: value })}
          language={language}
          height="250px"
          label="代码模板"
        />

        {/* 参考答案 - 使用Monaco Editor */}
        <CodeEditorWithMonaco
          value={editDraft.answerText || ''}
          onChange={(value) => updateDraft({ answerText: value })}
          language={language}
          height="250px"
          label="参考答案"
        />

        {/* 时间/内存限制 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">时间限制</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={editDraft.timeLimitMs || 1000}
                onChange={(e) => updateDraft({ timeLimitMs: parseInt(e.target.value) || 1000 })}
                className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm focus:border-blue-500 focus:outline-none"
              />
              <span className="text-sm text-gray-500">ms</span>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">内存限制</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={editDraft.memoryLimitMb || 128}
                onChange={(e) => updateDraft({ memoryLimitMb: parseInt(e.target.value) || 128 })}
                className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm focus:border-blue-500 focus:outline-none"
              />
              <span className="text-sm text-gray-500">MB</span>
            </div>
          </div>
        </div>

        {/* 测试用例管理 */}
        <TestCaseManager
          testCases={editDraft.testCases || []}
          onChange={(testCases) => updateDraft({ testCases })}
          onRunTest={handleRunTest}
          referenceCode={editDraft.answerText}
        />

        {/* 结构化评分要点 */}
        <ScoringPointsEditor
          config={structuredScoring}
          onChange={(config) => {
            updateDraft({
              structuredScoring: config,
              commonMistakes: config.commonMistakes
            });
          }}
        />

        {/* 评分方式 */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">评分方式</div>
          <select
            value={editDraft.gradingMode || 'oj_ai'}
            onChange={(e) => updateDraft({ gradingMode: e.target.value as any })}
            className="px-3 py-2 border border-gray-200 rounded text-sm focus:border-blue-500 focus:outline-none w-full"
          >
            {gradingModeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  // 根据题型渲染对应的编辑面板
  const renderEditorPanel = () => {
    // 仅处理客观题，非客观题由 UnifiedNonObjectiveEditor 处理
    switch (editDraft.type) {
      case 'single_choice':
      case 'multi_choice':
      case 'judge':
        return renderObjectiveEditor();
      default:
        return null;
    }
  };

  if (!question) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">点击左侧题型添加题目</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Question Header */}
      <div className="sticky top-0 z-30 bg-gradient-to-r from-blue-50 to-blue-100 p-3 sm:p-4 lg:p-6 border-b border-blue-200">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            <span className="px-2 py-0.5 lg:px-3 lg:py-1 bg-blue-600 text-white text-xs lg:text-sm font-medium rounded-full">
              {typeNames[question?.type] || '单选题'}
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button onClick={onPrevQuestion} disabled={currentIndex === 0} className="px-3 lg:px-4 py-1.5 lg:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all shadow-sm">
              <ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4" />上一题
            </button>
            <button onClick={onNextQuestion} disabled={currentIndex === totalCount - 1} className="px-3 lg:px-4 py-1.5 lg:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all shadow-sm">
              下一题<ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4 rotate-180" />
            </button>
          </div>
          <div className="flex items-center gap-1.5 lg:gap-2 ml-auto">
            {/* 编辑模式操作按钮组 - 与导航按钮明显区分 */}
{onInsertQuestion && (
  <button
    onClick={() => onInsertQuestion(question.id)}
    className="px-3 py-1.5 text-white text-xs font-medium rounded-lg bg-green-500 hover:bg-green-600 active:bg-green-700 transition-colors flex items-center gap-1.5 shadow-sm border border-green-400"
    title="在此题后插入新题"
  >
    <Plus className="w-3.5 h-3.5" />
    <span className="hidden sm:inline">插入新题</span>
  </button>
)}
{onCopyQuestion && (
  <button
    onClick={() => onCopyQuestion(question.id)}
    className="px-3 py-1.5 text-white text-xs font-medium rounded-lg bg-blue-500 hover:bg-blue-600 active:bg-blue-700 transition-colors flex items-center gap-1.5 shadow-sm border border-blue-400"
    title="复制本题"
  >
    <Copy className="w-3.5 h-3.5" />
    <span className="hidden sm:inline">复制</span>
  </button>
)}
{onDeleteQuestion && (
  <button
    onClick={() => {
      if (confirm('确定要删除本题吗?此操作不可撤销。')) {
        onDeleteQuestion(question.id);
      }
    }}
    className="px-3 py-1.5 text-white text-xs font-medium rounded-lg bg-red-500 hover:bg-red-600 active:bg-red-700 transition-colors flex items-center gap-1.5 shadow-sm border border-red-400"
    title="删除本题"
  >
    <Trash className="w-3.5 h-3.5" />
    <span className="hidden sm:inline">删除</span>
  </button>
)}
            {isEditing && onCancelEdit && (
              <button
                onClick={() => {
                  onCancelEdit();
                }}
                className="px-3 py-1.5 text-gray-700 text-xs font-medium rounded-lg bg-white hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center gap-1.5 shadow-sm border border-gray-300"
                title="取消编辑"
              >
                <X className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">取消</span>
              </button>
            )}
            {/* 编辑/完成切换按钮 */}
            {onEditQuestion && onExitEdit && onSaveQuestion && (
              <button
                onClick={async () => {
                  if (isEditing) {
                    await handleSave();
                  } else {
                    onEditQuestion(question.id);
                  }
                }}
                className={`px-3 py-1.5 text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 shadow-sm ${
                  isEditing
                    ? 'bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 border border-emerald-400'
                    : 'bg-orange-500 hover:bg-orange-600 active:bg-orange-700 border border-orange-400'
                }`}
              >
                {isEditing ? (
                  <>
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                    <span className="hidden sm:inline">完成</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    <span className="hidden sm:inline">编辑</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="pt-0 px-3 sm:px-4 lg:px-6 pb-3 sm:pb-4 lg:pb-6">
        {isEditing && editDraft ? (
          <div className="space-y-4">
            <div className="text-sm sm:text-base lg:text-lg font-medium text-gray-900 leading-relaxed">
              <div className="flex items-start gap-2">
                <span className="text-blue-600 font-bold flex-shrink-0">{currentIndex + 1}.</span>
                <div className="flex-1 min-w-0">
                  <QuillEditorWithImageResize
                    value={editDraft.content || editDraft.title || ''}
                    onChange={(content, widths) => {
                      const updates: Partial<Question> = {
                        content: content,
                      };
                      if (widths) {
                        updates.titleImageWidths = widths;
                      }
                      updateDraft(updates);
                    }}
                    questionId={editDraft.id}
                    imageWidths={editDraft.titleImageWidths || []}
                    applyRealtimeScaling={true}
                  />
                </div>
              </div>
            </div>
            {renderEditorPanel()}
          </div>
        ) : (
          <QuizQuestionTitle question={question} currentIndex={currentIndex} examQuestions={examQuestions} />
        )}
      </div>

      {/* Answer Area */}
      {!isEditing && (
        <div className="px-3 sm:px-4 lg:px-6 pb-3 sm:pb-4 lg:pb-6">
          {/* 选项布局工具栏 - 仅客观题显示 */}
          {question?.type !== 'scratch_coding' &&
           question?.type !== 'python_coding' &&
           question?.type !== 'cpp_coding' &&
           question?.options?.some(o => o.image || (o.images && o.images.length > 0)) && (
            <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              {/* 布局切换按钮 */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleOptionLayoutChange('grid')}
                  className={`p-1.5 rounded transition-colors ${
                    (question.optionLayout || 'grid') === 'grid'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="网格布局"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleOptionLayoutChange('list')}
                  className={`p-1.5 rounded transition-colors ${
                    question.optionLayout === 'list'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="列表布局"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              {/* 分隔线 */}
              <div className="w-px h-5 bg-gray-300" />

              {/* 缩放快捷按钮 */}
              {[25, 50, 75, 100].map(scale => (
                <button
                  key={scale}
                  onClick={() => handleQuickScaleChange(scale)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    currentUniformScale === scale
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {scale}%
                </button>
              ))}
            </div>
          )}

          <QuestionRenderer
            key={question.id}
            question={question}
            currentIndex={currentIndex}
            answers={{}}
            isSubmitted={false}
            isPreview={true}
            paperId=""
            currentUser={null}
            aiExplanations={{}}
            examQuestions={examQuestions}
            aiGradingEnabled={true}
            isAssignmentMode={false}
            answerMode={question.type === 'scratch_coding' ? 'upload' : undefined}
            onAnswerChange={() => {}}
            onTextAnswerChange={() => {}}
            onAIGrading={async () => ''}
            onExamQuestionsChange={onExamQuestionsChange}
            onHasWidthChangesChange={() => {}}
          />
        </div>
      )}

      {/* 题目预览弹窗 */}
      <QuestionPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        question={editDraft || question}
      />

      {/* 选项图片上传弹窗 */}
      {showOptionImageModal && optionImageUploadTarget && (
        <ImageUploadModal
          target={{ questionId: '', optionId: optionImageUploadTarget.optionId }}
          onClose={() => {
            setShowOptionImageModal(false);
            setOptionImageUploadTarget(null);
          }}
          onUpload={(imageData) => handleOptionImageUploadComplete([imageData])}
          allowMultiple={true}
        />
      )}
    </div>
  );
}
